import { createSlice } from '@reduxjs/toolkit';
// utils
import { Api } from '../../utils/api';
import { COURSE_URLS, STUDENT_WALL_URLS, SERVICE_URLS, TEAM_URLS, GOOGLE_REVIEWS_URLS, SUCCESS_STORIES_URLS, ACHIEVERS_URLS, ANNOUNCEMENT_URLS } from '../../utils/api/urls';
// utils
import { loadGoogleReviews } from '../../utils/googlePlaces';
// ----------------------------------------------------------------------

// The faker-generated demo data is only needed when an endpoint fails, so it is
// imported on demand. A static import put faker and the whole generated dataset
// into the main bundle that every visitor downloads.
const loadMockWeb = () => import('../../_mock/web');

const initialState = {
  isLoading: false,
  error: false,
  hasMore: false,
  courses: [],
  studentwalls: [],
  services: [],
  teams: [],
  course: null,
  service: null,
  index: 0,
  step: 11,
  tags: [],
  googleReviews: [],
  googleReviewMeta: null,
  successStories: [],
  achievers: [],
  announcements: [],
};

const slice = createSlice({
  name: 'course',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // HAS MORE
    noHasMore(state) {
      state.hasMore = false;
    },

    getMoreCourses(state) {
      const setIndex = state.index + state.step;
      state.index = setIndex;
    },


    // GET COURSES
    getCourseListSuccess(state, action) {
      state.isLoading = false;
      // The courses endpoint returns `{ results: [...] }` while the rest of the
      // app consumes `courses` as a plain array, so normalise it here.
      const payload = action.payload ?? [];
      state.courses = Array.isArray(payload) ? payload : payload.results ?? [];
    },

    // GET STUDENT WALLS
    getStudentWallListSuccess(state, action) {
      state.isLoading = false;
      state.studentwalls = action.payload;
    },
    // GET STUDENT WALLS
    getTeamListSuccess(state, action) {
      state.isLoading = false;
      state.teams = action.payload;
    },
    // 
    // GET SERVICES
    getServiceListSuccess(state, action) {
      state.isLoading = false;
      state.services = action.payload;
    },
    // GET SERVICE
    getServiceSuccess(state, action) {
      state.isLoading = false;
      state.service = action.payload;
    },
    // GET COURSE
    getCourseSuccess(state, action) {
      state.isLoading = false;
      state.course = action.payload;
    },

    // GET TAG 
    getTagsSuccess(state, action) {
      state.isLoading = false;
      state.tags = action.payload;
    },

    // GET GOOGLE REVIEWS
    getGoogleReviewsSuccess(state, action) {
      state.isLoading = false;
      state.googleReviews = action.payload;
    },

    // True average and total from Place Details, shaped like the backend
    // `review_meta`: { rating, totalReviews, capturedAt }. The API returns at
    // most five reviews, so `googleReviews.length` cannot stand in for the total,
    // and no figure here is ever computed from the cards.
    getGoogleReviewsMeta(state, action) {
      state.googleReviewMeta = action.payload;
    },

    // GET SUCCESS STORIES
    getSuccessStoriesSuccess(state, action) {
      state.isLoading = false;
      state.successStories = action.payload;
    },

    // GET ACHIEVERS
    getAchieversSuccess(state, action) {
      state.isLoading = false;
      state.achievers = action.payload;
    },

    // GET ANNOUNCEMENTS
    getAnnouncementListSuccess(state, action) {
      state.isLoading = false;
      // Tolerate a paginated `{ results: [...] }` body as well as a plain
      // array, so the ribbon works whichever shape the endpoint returns.
      const payload = action.payload ?? [];
      state.announcements = Array.isArray(payload) ? payload : payload.results ?? [];
    },

  }
});

// Reducer
export default slice.reducer;

// Actions
export const { getMoreCourses } = slice.actions;

// Exported so the homepage bundle (slices/homeBundle.js) can fill these sections
// in one request without duplicating the normalisation each action already does.
export const {
  getCourseListSuccess,
  getStudentWallListSuccess,
  getTeamListSuccess,
  getServiceListSuccess,
  getGoogleReviewsSuccess,
  // Also filled by the homepage bundle, with the backend's stored aggregate
  // (ASAP-RVS-005) rather than a live Places lookup.
  getGoogleReviewsMeta,
  getSuccessStoriesSuccess,
  getAnnouncementListSuccess,
} = slice.actions;

// ----------------------------------------------------------------------

export function getAllCourses() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await Api.unget(COURSE_URLS.list);
      dispatch(slice.actions.getCourseListSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getStudentwalls(course) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      let couseparam = ''
      if (course) {
        couseparam = `?course=${course}`;
      }
      const response = await Api.unget(`${STUDENT_WALL_URLS.list}${couseparam}`);
      dispatch(slice.actions.getStudentWallListSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getTeams() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await Api.unget(TEAM_URLS.list);
      dispatch(slice.actions.getTeamListSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getServices() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await Api.unget(SERVICE_URLS.list);
      dispatch(slice.actions.getServiceListSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

//----------------------------------------------------------------------------

export function getCourse(courseslug) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await Api.unget(`${COURSE_URLS.get}${courseslug}/`);
      dispatch(slice.actions.getCourseSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
//----------------------------------------------------------------------------

export function getService(serviceslug) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await Api.unget(`${SERVICE_URLS.get}${serviceslug}/`);
      dispatch(slice.actions.getServiceSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
// 
//----------------------------------------------------------------------------

// Google reviews. The backend endpoint does not exist yet (ASAP-FU-020 tracks
// it), so on failure the live reviews are read straight from the Google Places
// API using the place id, and only then from the bundled snapshot. That keeps
// the section showing real reviews without a backend.
export function getGoogleReviews() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await Api.unget(GOOGLE_REVIEWS_URLS.list);
      const payload = response.data ?? {};
      const reviews = Array.isArray(payload.reviews) ? payload.reviews : payload;
      dispatch(slice.actions.getGoogleReviewsSuccess(reviews));

      if (payload.review_meta && typeof payload.review_meta === 'object') {
        dispatch(slice.actions.getGoogleReviewsMeta(payload.review_meta));
      }
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));

      const { reviews, meta } = await loadGoogleReviews();

      if (reviews.length) {
        dispatch(slice.actions.getGoogleReviewsSuccess(reviews));
        dispatch(slice.actions.getGoogleReviewsMeta(meta));
        return;
      }

      const { googleReviews } = await loadMockWeb();

      dispatch(slice.actions.getGoogleReviewsSuccess(googleReviews));
    }
  };
}

// YouTube success stories — falls back to _mock data when the endpoint is
// unavailable (ASAP-FU-021 tracks the live endpoint).
export function getSuccessStories() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await Api.unget(SUCCESS_STORIES_URLS.list);
      dispatch(slice.actions.getSuccessStoriesSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      const { successStories } = await loadMockWeb();

      dispatch(slice.actions.getSuccessStoriesSuccess(successStories));
    }
  };
}

// Pass-out achievers — falls back to _mock data when the endpoint is
// unavailable (ASAP-FU-022 tracks the live endpoint).
export function getAchievers(course) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      let courseParam = '';
      if (course) {
        courseParam = `?course=${course}`;
      }
      const response = await Api.unget(`${ACHIEVERS_URLS.list}${courseParam}`);
      dispatch(slice.actions.getAchieversSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      const { achievers } = await loadMockWeb();

      dispatch(slice.actions.getAchieversSuccess(achievers));
    }
  };
}

//----------------------------------------------------------------------------

// Nav ribbon marquee items — falls back to _mock data when the endpoint is
// unavailable, so the ribbon keeps rendering until the backend ships.
export function getAnnouncements() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await Api.unget(ANNOUNCEMENT_URLS.list);
      dispatch(slice.actions.getAnnouncementListSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      const { announcements } = await loadMockWeb();

      dispatch(slice.actions.getAnnouncementListSuccess(announcements));
    }
  };
}

